/**
 * React Query bindings over `src/data/api.ts` for tabs not yet migrated.
 *
 * Create + session live under `src/api/hooks/` — re-exported here so existing
 * imports keep working. New tab work should add `src/api/hooks/<tab>/`.
 */

import { useQuery } from '@tanstack/react-query'
import * as api from '@/data/api'
import type { GameBucket, MarketType, PlTypeName, Role, Sport } from '@/data/types'
import { ROLE_LABEL, descendantRoles } from '@/data/types'
import { keys } from '@/api/hooks/shared/keys'

export { keys }
export { useSession, type SessionView } from '@/api/hooks/shared/useSession'
export { useOverview } from '@/api/hooks/shared/useOverview'
export { useCreateAccount } from '@/api/hooks/create/useCreateAccount'
export { useUpdateAccount } from '@/api/hooks/member/useUpdateAccount'
export { useDownline } from '@/api/hooks/downline/useDownline'
export { useChildren } from '@/api/hooks/downline/useChildren'
export {
  useBulkSettle,
  type BulkRunRow,
  type BulkRunState,
  type RowOutcome,
} from '@/api/hooks/downline/useBulkSettle'
export { useSetAccountStatus, useStopImpact } from '@/api/hooks/downline/useAccountStatus'
export { useAccount } from '@/api/hooks/member/useAccount'
export { useMemberDetail } from '@/api/hooks/member/useMemberDetail'
export { useAllTm } from '@/api/hooks/member/useAllTm'
export { useMemberBalance } from '@/api/hooks/member/useMemberBalance'
export { useMemberActivityMatrix } from '@/api/hooks/member/useMemberActivityMatrix'
export { useGames } from '@/api/hooks/reports/useGames'
export { useSubGames } from '@/api/hooks/reports/useSubGames'
export { useEventsForPll } from '@/api/hooks/reports/useEventsForPll'
export { useCurrencies } from '@/api/hooks/reports/useCurrencies'
export { useTransferCredit } from '@/api/hooks/credit/useTransferCredit'
export { useCreditEventStream } from '@/api/hooks/credit/useCreditEventStream'
export { usePnl } from '@/api/hooks/reports/usePnl'
export { useMemberPnl } from '@/api/hooks/reports/useMemberPnl'
export { usePlStatement } from '@/api/hooks/reports/usePlStatement'
export { useOneAccount } from '@/api/hooks/reports/useOneAccount'
export { useTransfers } from '@/api/hooks/reports/useTransfers'
export { useTurnover } from '@/api/hooks/reports/useTurnover'
export { useCommission } from '@/api/hooks/reports/useCommission'
export { useFancyBookmakerCommission } from '@/api/hooks/reports/useFancyBookmakerCommission'
export { useCommissionEligibility } from '@/api/hooks/reports/useCommissionEligibility'
export { useCommissionChildUsers } from '@/api/hooks/reports/useCommissionChildUsers'
export { useActivity } from '@/api/hooks/shared/useActivity'
export {
  useDeleteMemberBlockedDeviceId,
  useLoginHistory,
  useSaveMemberBlockedDeviceId,
} from '@/api/hooks/member/useLoginHistory'
export { useConsoleSearch } from '@/api/hooks/search/useConsoleSearch'

export function useExposure(query: api.ExposureQuery) {
  return useQuery({
    queryKey: keys.exposure(query),
    queryFn: () => api.getExposure(query),
    refetchInterval: api.POLL_INTERVAL_MS,
    placeholderData: (previous) => previous,
  })
}

export function useMarketBook(marketId: string | undefined, basis: 'my' | 'total' = 'total') {
  return useQuery({
    queryKey: keys.marketBook(marketId ?? '', basis),
    queryFn: () => api.getMarketBook(marketId!, { basis }),
    enabled: Boolean(marketId),
    refetchInterval: api.POLL_INTERVAL_MS,
    placeholderData: (previous) => previous,
  })
}

/**
 * One expanded level of the market's hierarchy tree.
 *
 * Polls on the SAME interval as the root. Different intervals across one table
 * would let a parent row and its children disagree, and the first thing an
 * operator does with a hierarchy is check that the children add up.
 */
export function useMarketBookBranch(
  marketId: string | undefined,
  parentId: string | null,
  basis: 'my' | 'total' = 'total',
) {
  return useQuery({
    queryKey: keys.marketBookBranch(marketId ?? '', parentId ?? '', basis),
    queryFn: () => api.getMarketBookBranch(marketId!, parentId!, { basis }),
    enabled: Boolean(marketId && parentId),
    refetchInterval: api.POLL_INTERVAL_MS,
  })
}

/** The market bet LOG. Separate from `useMarketBook` because the log is a record
 *  and can show settled, void and cancelled rows the projection never can. */
export function useMarketBets(query: api.BetQuery) {
  return useQuery({
    queryKey: keys.bets(query),
    queryFn: () => api.listBets(query),
    placeholderData: (previous) => previous,
  })
}

export { useBets } from '@/api/hooks/member/useBets'
export { useMemberBets } from '@/api/hooks/member/useMemberBets'
export { useStatement } from '@/api/hooks/member/useStatement'

/**
 * Role chips for the downline toolbar, scoped to roles that can sit beneath the
 * signed-in actor. Company Master sees TM…Member; Double Master sees SM…Member.
 */
export function roleFiltersFor(actor: Role | undefined): { id: Role; label: string }[] {
  if (!actor) return []
  return descendantRoles(actor).map((id) => ({ id, label: ROLE_LABEL[id] }))
}

/** @deprecated Prefer `roleFiltersFor(session.role)` — static list assumed Super Master. */
export const ROLE_FILTERS: { id: Role; label: string }[] = roleFiltersFor('super-master')

export const SPORT_FILTERS: Sport[] = [
  'Cricket',
  'Soccer',
  'Tennis',
  'Horse Racing',
  'Greyhound Racing',
  'Casino',
]

export const GAME_FILTERS: GameBucket[] = [
  'SportsBook',
  'SportsBook - Bookmaker',
  'Fancy',
  'Racing',
  'Live Casino',
  'Crash Game',
  'Card Game',
  'Credit Limit',
]

/** Lab PL Statement Select Type — same list as `PLStatementCtrl.getTypes`. */
export const TYPE_FILTERS: PlTypeName[] = [
  'Match Odds',
  'Completed Match',
  'Tied Match',
  'Bookmaker',
  'Session',
  'Linesession',
]

/**
 * `main` and `fancy` are SYNTHETIC buckets the page expands into concrete
 * `MarketType[]` before it calls the query — an operator thinks "show me only
 * the fancy book", not every fancy market id. Keeping the vocabulary here
 * beside SPORT_FILTERS and ROLE_FILTERS means the command palette can build
 * links against it without importing a feature module.
 */
export const MARKET_TYPE_FILTERS: { id: 'all' | 'main' | 'fancy' | MarketType; label: string }[] = [
  { id: 'all', label: 'All markets' },
  { id: 'main', label: 'Main markets (odds, bookmaker, toss)' },
  { id: 'fancy', label: 'Fancy & session lines' },
  { id: 'Match Odds', label: 'Match Odds' },
  { id: 'Bookmaker', label: 'Bookmaker' },
  { id: 'Toss Market', label: 'Toss Market' },
  { id: 'Fancy', label: 'Fancy' },
  { id: 'Live Casino', label: 'Live Casino' },
]

/** The concrete market types behind each synthetic bucket. One definition, so a
 *  toolbar and a palette-built link cannot disagree about what "fancy" means. */
export const MARKET_TYPE_BUCKETS: Record<'main' | 'fancy', MarketType[]> = {
  main: ['Match Odds', 'Bookmaker', 'Toss Market'],
  fancy: ['Fancy'],
}

export const EXPOSURE_SORTS: { id: api.ExposureSort; label: string }[] = [
  { id: 'worst', label: 'Worst case first' },
  { id: 'start', label: 'Starting soonest' },
  { id: 'event', label: 'Event name' },
  { id: 'matched', label: 'Most matched' },
]

export { POLL_INTERVAL_MS } from '@/data/api'
